/**
 * SiteVlanRollout -- the reusable pieces of the site VLAN rollout (was routes/SiteVlans).
 *
 * Owner rule (2026-09-13): a VLAN added to a site is configured on the site's gateway(s) AND its
 * switches in the same change. Owner request (2026-09-14): that table lives in Gateway > Configure
 * > VLANs (routes/GatewayVlans, Meraki "Addressing & VLANs" layout). This module holds the parts
 * that page composes, so the rollout logic exists exactly once:
 *
 *   SiteVlanDialog  add / edit one site VLAN. Saving queues the site's automatic deploy
 *                   (services/site_vlan_auto_deploy); the dialog itself touches no device.
 *   HaPairCard      the gateway HA pair (which box is .2 and which .3).
 *   ApNativeCard    the AP management (native) VLAN AP-facing switch ports carry untagged.
 *   RolloutPanel    The deploy plan. Its shape follows the site's auto-deploy state (lib/rolloutMode):
 *                   with auto-deploy ON (the default) it is a READ-ONLY PREVIEW -- no confirm gate,
 *                   because the owner's rule (2026-09-14) is that VLANs ALWAYS sync to devices
 *                   automatically. The preview covers every device -- gateway networks with
 *                   addresses and DHCP ranges, the exact switch commands, every port's trunk
 *                   decision WITH the LLDP neighbour that justified it (and the per-port override
 *                   dropdown), refusals and warnings -- plus the live "Last deploy" results.
 *                   The manual review checkbox + "Apply to N devices" button survive ONLY when an
 *                   operator turned auto-deploy OFF for the site (they opted into applying by hand);
 *                   apply then sends the plan token and the server re-plans and refuses if anything
 *                   moved. A plain "Retry now" (no checkbox) shows when the last automatic run
 *                   failed/was refused. Progress is polled per device, then Verify.
 *                   OFFLINE SWITCHES (owner decision 2026-10-13): an offline switch still refuses
 *                   the plan by default; "Skip offline switches" re-plans without them, lists
 *                   what each would have received, and the apply needs one tick PER skipped
 *                   switch. `scopeSwitch` turns the panel into the one-switch re-plan of a
 *                   switch flagged "pending VLAN changes" (PendingSwitchesCard).
 *  PendingSwitchesCard  switches skipped while offline whose VLANs are still pending, with the
 *                   one-click "Re-plan this switch" once they are back online (never automatic).
 *
 * Design system: surfaces and ink tokens for structure; the status ramp ONLY for operational
 * state (refusal = critical, warning = warn, applied/verified = healthy); brand violet only for
 * interactive affordances. Tablet-first: cards stack, tables scroll inside their own container.
 * Switch ports are shown ONE-based (lib/switchPorts) while every request keeps the uCentral name.
 */
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Link } from "react-router-dom";
import { useSession } from "@/stores/session";
import { useDevices } from "@/lib/api/hooks";
import { ApiError } from "@/lib/api/client";
import {
  useApplyVlanRollout,
  useAutoDeploy,
  useDevicePendingVlans,
  usePendingSwitches,
  useGatewayHaPair,
  useRetryAutoDeploy,
  useSaveGatewayHaPair,
  useSaveSiteVlan,
  useSaveSwitchSettings,
  useSetTrunkOverride,
  useSwitchSettings,
  useVerifyVlanRollout,
  useVlanRolloutPlan,
  useVlanRolloutStatus,
  type DhcpSource,
  type PendingSwitch,
  type RolloutDeviceResult,
  type RolloutGatewayPlan,
  type RolloutItem,
  type RolloutPortDecision,
  type RolloutSwitchPlan,
  type SkippedOfflineSwitch,
  type SiteVlanRow,
  type TrunkOverrideMode,
  type VlanRollout,
  type VlanRolloutPlan,
} from "@/lib/api/siteVlans";
import { ErrorBanner } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { StatusPill } from "@/components/ui/StatusPill";
import { RoleGate } from "@/components/auth/RoleGate";
import { formatPortRefs, formatSwitchPort } from "@/lib/switchPorts";
import { rolloutCopy, rolloutMode, showsManualApply, showsRetry } from "@/lib/rolloutMode";
import { verificationMissingLines, verificationWord } from "@/lib/siteVlanApplied";
import {
  ackComplete,
  ackLabel,
  pendingLine,
  skipOffer,
  skippedLine,
  verificationDeviceLabel,
} from "@/lib/skipOffline";
import { RolloutPathLines } from "@/components/site/VlanPathCheck";
import { cn } from "@/lib/cn";
import type { HealthState } from "@/types/domain";

/** Shared input chrome (matches GatewayChrome INPUT). */
const INPUT =
  "min-h-touch rounded border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand";
/** Site VLANs, settings, overrides, apply and verify are MSP-level writes server-side. */
export const WRITE_ROLE = "msp_admin" as const;
export const WRITE_REASON = "Site VLANs and their rollout require an MSP administrator.";

/* =========================================================================================
 * 1. Site VLAN add / edit dialog (the table itself is in routes/GatewayVlans)
 * ========================================================================================= */

export function SiteVlanDialog({ siteId, row, onClose }: { siteId: string; row: SiteVlanRow | null; onClose: () => void }) {
  const save = useSaveSiteVlan(siteId);
  const [vlanId, setVlanId] = useState(row ? String(row.vlan_id) : "");
  const [name, setName] = useState(row?.vlan_name ?? "");
  const [subnet, setSubnet] = useState(row?.subnet ?? "");
  const [gatewayIp, setGatewayIp] = useState(row?.gateway_ip ?? "");
  const [dhcp, setDhcp] = useState<DhcpSource>(row?.dhcp_source ?? "site");
  const [lease, setLease] = useState(row?.dhcp_lease_time ?? "");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate(
      {
        editVlanId: row ? row.vlan_id : null,
        body: {
          ...(row ? {} : { vlan_id: Number(vlanId) }),
          vlan_name: name.trim(),
          subnet: subnet.trim() || null,
          gateway_ip: gatewayIp.trim() || null,
          dhcp_source: dhcp,
          dhcp_lease_time: lease.trim() || null,
        },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content
          aria-describedby={undefined}
          className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[90vh] w-[min(94vw,30rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-md border border-line bg-overlay p-5 shadow-sheet outline-none"
        >
          <Dialog.Title className="text-base font-semibold text-ink">
            {row ? `Edit VLAN ${row.vlan_id}` : "Add site VLAN"}
          </Dialog.Title>
          <p className="mt-1 text-xs text-ink-muted">
            Deploys to every gateway and switch at the site shortly after you save. Changes made together go out
            as one deploy.
          </p>
          <form onSubmit={submit} className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <Field label="VLAN ID">
              <input
                className={INPUT}
                inputMode="numeric"
                value={vlanId}
                disabled={!!row}
                onChange={(e) => setVlanId(e.target.value.replace(/[^0-9]/g, ""))}
                required
              />
            </Field>
            <Field label="Name">
              <input className={INPUT} value={name} maxLength={32} onChange={(e) => setName(e.target.value)} required />
            </Field>
            <Field label="Subnet" hint="Blank = layer 2 only (switches carry it, no gateway interface)">
              <input className={cn(INPUT, "font-mono")} placeholder="10.150.150.0/24" value={subnet} onChange={(e) => setSubnet(e.target.value)} />
            </Field>
            <Field label="Gateway IP" hint="Blank = .1 (single gateway) or the HA pair's .2/.3">
              <input className={cn(INPUT, "font-mono")} value={gatewayIp} onChange={(e) => setGatewayIp(e.target.value)} />
            </Field>
            <Field label="DHCP">
              <select className={INPUT} value={dhcp} onChange={(e) => setDhcp(e.target.value as DhcpSource)}>
                <option value="site">Gateway serves it</option>
                <option value="infoblox">External DHCP</option>
                <option value="none">No DHCP</option>
              </select>
            </Field>
            <Field label="Lease time">
              <input className={INPUT} placeholder="6h" value={lease} onChange={(e) => setLease(e.target.value)} />
            </Field>
            {save.error && (
              <div className="col-span-2">
                <ErrorBanner error={save.error} />
              </div>
            )}
            <div className="md-dialog-footer col-span-2 flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={save.isPending || !name.trim() || (!row && !vlanId)}>
                {save.isPending ? "Saving…" : row ? "Save VLAN" : "Add VLAN"}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/* =========================================================================================
 * 2. Settings — gateway HA pair, AP management (native) VLAN
 * ========================================================================================= */

export function HaPairCard({ siteId }: { siteId: string }) {
  const { data } = useGatewayHaPair(siteId);
  const save = useSaveGatewayHaPair(siteId);
  const { data: devices } = useDevices({ kind: "gateway", limit: 100 });
  const gateways = devices?.items ?? [];
  const pair = data?.ha_pair ?? null;
  const [primary, setPrimary] = useState("");
  const [secondary, setSecondary] = useState("");

  // Seed the pickers from the stored pair once it arrives (and after a save).
  useEffect(() => {
    setPrimary(pair?.primary?.serial ?? "");
    setSecondary(pair?.secondary?.serial ?? "");
  }, [pair?.primary?.serial, pair?.secondary?.serial]);

  const dirty = primary !== (pair?.primary?.serial ?? "") || secondary !== (pair?.secondary?.serial ?? "");

  return (
    <section className="md-card p-4">
      <h2 className="text-sm font-semibold text-ink">Gateway HA pair</h2>
      <p className="mt-0.5 text-xs text-ink-muted">
        With a pair, every VLAN gets VIP .1, primary .2, secondary .3 and split DHCP (10–129 / 130–249). Without
        one, a single gateway takes .1.
      </p>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Primary">
          <GatewaySelect value={primary} onChange={setPrimary} gateways={gateways} />
        </Field>
        <Field label="Secondary">
          <GatewaySelect value={secondary} onChange={setSecondary} gateways={gateways} />
        </Field>
      </div>
      {save.error && (
        <div className="mt-3">
          <ErrorBanner error={save.error} />
        </div>
      )}
      <RoleGate min={WRITE_ROLE} mode="hide">
        <div className="mt-3 flex flex-wrap justify-end gap-2">
          {pair && (
            <Button variant="ghost" disabled={save.isPending} onClick={() => save.mutate(null)}>
              Remove pair
            </Button>
          )}
          <Button
            variant="secondary"
            disabled={!dirty || !primary || !secondary || primary === secondary || save.isPending}
            onClick={() => save.mutate({ primary_serial: primary, secondary_serial: secondary })}
          >
            Save pair
          </Button>
        </div>
      </RoleGate>
    </section>
  );
}

function GatewaySelect({
  value,
  onChange,
  gateways,
}: {
  value: string;
  onChange: (v: string) => void;
  gateways: { serial: string; name: string }[];
}) {
  return (
    <select className={INPUT} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">—</option>
      {gateways.map((g) => (
        <option key={g.serial} value={g.serial}>
          {g.name} ({g.serial})
        </option>
      ))}
    </select>
  );
}

export function ApNativeCard({ siteId }: { siteId: string }) {
  const { data } = useSwitchSettings(siteId);
  const save = useSaveSwitchSettings(siteId);
  const settings = data?.switch_settings;
  const [vlan, setVlan] = useState("");
  const [upstream, setUpstream] = useState(false);

  useEffect(() => {
    setVlan(settings?.ap_native_vlan != null ? String(settings.ap_native_vlan) : "");
    setUpstream(!!settings?.ap_native_served_upstream);
  }, [settings?.ap_native_vlan, settings?.ap_native_served_upstream]);

  const dirty =
    vlan !== (settings?.ap_native_vlan != null ? String(settings.ap_native_vlan) : "") ||
    upstream !== !!settings?.ap_native_served_upstream;

  return (
    <section className="md-card p-4">
      <h2 className="text-sm font-semibold text-ink">AP management VLAN</h2>
      <p className="mt-0.5 text-xs text-ink-muted">
        Carried <strong className="font-medium text-ink">untagged</strong> on switch ports whose LLDP neighbour is an
        access point (or marked AP port), with the site VLANs tagged. Switch, gateway and foreign-switch ports keep
        native VLAN 1. Blank leaves AP ports as they are.
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <Field label="Native VLAN">
          <input
            className={cn(INPUT, "w-28 font-mono")}
            inputMode="numeric"
            placeholder="unchanged"
            value={vlan}
            onChange={(e) => setVlan(e.target.value.replace(/[^0-9]/g, ""))}
          />
        </Field>
        <label className="flex min-h-touch items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={upstream} onChange={(e) => setUpstream(e.target.checked)} />
          DHCP for it is served upstream
        </label>
      </div>
      {save.error && (
        <div className="mt-3">
          <ErrorBanner error={save.error} />
        </div>
      )}
      <RoleGate min={WRITE_ROLE} mode="hide">
        <div className="mt-3 flex justify-end">
          <Button
            variant="secondary"
            disabled={!dirty || save.isPending}
            onClick={() =>
              save.mutate({ ap_native_vlan: vlan ? Number(vlan) : null, ap_native_served_upstream: upstream })
            }
          >
            Save
          </Button>
        </div>
      </RoleGate>
    </section>
  );
}

/* =========================================================================================
 * 3. Rollout — review, apply, progress, verify
 * ========================================================================================= */

/**
 * The rollout review. `initialReviewing` starts it already fetching the plan (the Gateway VLANs
 * "Review & apply to all devices" dialog); otherwise it waits for "Review changes".
 */
export function RolloutPanel({
  siteId,
  initialReviewing = false,
  scopeSwitch = null,
}: {
  siteId: string;
  initialReviewing?: boolean;
  /** Re-plan ONE switch (flagged "pending VLAN changes" after being skipped offline). */
  scopeSwitch?: string | null;
}) {
  const [reviewing, setReviewing] = useState(initialReviewing);
  const [confirmed, setConfirmed] = useState(false);
  // Owner decision 2026-10-13: skipping offline switches is an explicit operator choice, and
  // every skipped switch is acknowledged by name (one tick each) before apply is enabled.
  const [skipOffline, setSkipOffline] = useState(false);
  const [acked, setAcked] = useState<ReadonlySet<string>>(new Set());
  const plan = useVlanRolloutPlan(siteId, reviewing, {
    skipOffline: skipOffline && !scopeSwitch,
    switchSerial: scopeSwitch,
  });
  const status = useVlanRolloutStatus(siteId);
  const apply = useApplyVlanRollout(siteId);
  const verify = useVerifyVlanRollout(siteId);
  const rollout = status.data?.rollout ?? null;
  const running = rollout?.status === "running";
  const deviceCount = plan.data ? plan.data.apply_order.length : 0;

  // Auto-deploy state decides the modal's shape (lib/rolloutMode): with it ON (the default) this
  // is a READ-ONLY preview -- no confirm gate, because the owner's rule is that VLANs always sync
  // automatically. The manual review+apply gate survives ONLY when an operator turned auto-deploy
  // off, and a plain "Retry now" survives when the last automatic run failed or was refused.
  const auto = useAutoDeploy(siteId);
  const autoView = auto.data?.auto_deploy ?? null;
  const autoEnabled = autoView?.enabled ?? true;
  const lastRunStatus = autoView?.last_run?.status ?? null;
  const mode = rolloutMode(autoEnabled, lastRunStatus);
  const copy = rolloutCopy(mode);
  const retry = useRetryAutoDeploy(siteId);

  // A new plan (token) must be confirmed again: a tick on an older review proves nothing.
  const token = plan.data?.plan_token;
  useEffect(() => {
    setConfirmed(false);
    setAcked(new Set());
  }, [token]);
  const offer = skipOffer(plan.data);
  const skipping = !!plan.data?.skip_offline;
  const scoped = !!plan.data?.scope;

  const applyRefusals = refusalsOf(apply.error);

  return (
    <section className="md-card overflow-hidden">
      <CardHeader
        title={copy.title}
        hint={copy.subtitle}
        action={
          <Button
            variant="secondary"
            disabled={plan.isFetching || running}
            onClick={() => (reviewing ? void plan.refetch() : setReviewing(true))}
          >
            {plan.isFetching ? "Reading devices…" : plan.data ? "Refresh preview" : "Preview changes"}
          </Button>
        }
      />

      <div className="flex flex-col gap-4 p-4">
        {plan.error && <ErrorBanner error={plan.error} />}
        {plan.data && <PlanView plan={plan.data} siteId={siteId} busy={plan.isFetching} />}

        {/* OFFLINE SWITCHES: the default plan refuses; offer the explicit skip. */}
        {offer.offer && (
          <SkipOfferBox
            switches={offer.switches}
            unblocks={offer.unblocks}
            busy={plan.isFetching}
            onSkip={() => setSkipOffline(true)}
          />
        )}

        {/* SKIP plan: each skipped switch, what it misses, one acknowledgement per switch. Shown
            in every auto-deploy mode -- skipping is an explicit manual decision, never automatic. */}
        {skipping && plan.data && (
          <SkippedApply
            plan={plan.data}
            acked={acked}
            onToggle={(serial, on) =>
              setAcked((prev) => {
                const next = new Set(prev);
                if (on) next.add(serial);
                else next.delete(serial);
                return next;
              })
            }
            busy={running || apply.isPending || plan.isFetching}
            pending={apply.isPending}
            onCancel={() => setSkipOffline(false)}
            onApply={() =>
              apply.mutate({ planToken: plan.data!.plan_token, skipOffline: true, acknowledgeSkipped: [...acked] })
            }
          />
        )}

        {/* ONE-SWITCH re-plan of a switch with pending VLAN changes: review, tick, apply. */}
        {scoped && plan.data && (
          <ScopedApply
            plan={plan.data}
            confirmed={confirmed}
            onConfirm={setConfirmed}
            busy={running || apply.isPending || plan.isFetching}
            pending={apply.isPending}
            onApply={() => apply.mutate({ planToken: plan.data!.plan_token, switchSerial: plan.data!.scope!.switch })}
          />
        )}

        {(skipping || scoped) && apply.error && (
          <div className="flex flex-col gap-2">
            <ErrorBanner error={apply.error} />
            {applyRefusals.length > 0 && <ItemList items={applyRefusals} tone="critical" names={{}} />}
          </div>
        )}

        {/* MANUAL path only (auto-deploy off): the operator opted in to applying by hand, so the
            review checkbox + "Apply to N devices" button stay exactly as before. (Not for a skip
            or one-switch plan: those carry their own apply above.) */}
        {showsManualApply(mode) && plan.data && !skipping && !scoped && (
          <>
            <div className="flex flex-col gap-3 rounded-md border border-line bg-surface-2 p-3 sm:flex-row sm:items-center sm:justify-between">
              <label className="flex min-h-touch items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  checked={confirmed}
                  disabled={!plan.data.can_apply}
                  onChange={(e) => setConfirmed(e.target.checked)}
                />
                I reviewed the changes on all {deviceCount} device{deviceCount === 1 ? "" : "s"}
              </label>
              <RoleGate min={WRITE_ROLE} mode="disable" reason={WRITE_REASON}>
                <Button
                  variant="primary"
                  disabled={!plan.data.can_apply || !confirmed || running || apply.isPending || plan.isFetching}
                  onClick={() => apply.mutate(plan.data!.plan_token)}
                >
                  {apply.isPending ? "Starting…" : `Apply to ${deviceCount} device${deviceCount === 1 ? "" : "s"}`}
                </Button>
              </RoleGate>
            </div>
            {!plan.data.can_apply && (
              <p className="text-xs text-critical">Apply is blocked until every refusal above is resolved.</p>
            )}
            {apply.error && (
              <div className="flex flex-col gap-2">
                <ErrorBanner error={apply.error} />
                {applyRefusals.length > 0 && <ItemList items={applyRefusals} tone="critical" names={{}} />}
              </div>
            )}
          </>
        )}

        {/* RETRY only (auto-deploy on, last automatic run failed/refused): re-queue the SAME
            automatic plan. No review checkbox -- the operator is re-running the automatic deploy,
            not hand-approving a new plan. The reason shows in "Last deploy" below. */}
        {showsRetry(mode) && (
          <div className="flex flex-col gap-3 rounded-md border border-line bg-surface-2 p-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-ink-muted">
              The last automatic deploy did not finish. Retry it once the reason below is resolved.
            </p>
            <div className="flex flex-col items-end gap-1">
              <RoleGate min={WRITE_ROLE} mode="disable" reason={WRITE_REASON}>
                <Button variant="primary" disabled={retry.isPending || running} onClick={() => retry.mutate()}>
                  {retry.isPending ? "Queuing…" : "Retry now"}
                </Button>
              </RoleGate>
              {retry.error && <ErrorBanner error={retry.error} />}
            </div>
          </div>
        )}

        {rollout && (
          <RolloutProgress
            rollout={rollout}
            verifying={verify.isPending}
            verifyError={verify.error}
            onVerify={() => verify.mutate()}
          />
        )}
      </div>
    </section>
  );
}

/** The default plan is refused by offline switches: offer to skip them (explicit choice). */
function SkipOfferBox({
  switches,
  unblocks,
  busy,
  onSkip,
}: {
  switches: { serial: string; name: string }[];
  unblocks: boolean;
  busy: boolean;
  onSkip: () => void;
}) {
  const n = switches.length;
  return (
    <div className="flex flex-col gap-3 rounded-md border border-warn/40 bg-warn-soft p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 text-sm text-ink">
        <p className="font-medium">
          {n} switch{n === 1 ? " is" : "es are"} offline: {switches.map((s) => s.name).join(", ")}
        </p>
        <p className="mt-0.5 text-xs text-ink-muted">
          An offline switch stops the whole deploy. You can skip offline switches instead: they will not get the
          VLANs now and are flagged "pending VLAN changes" until you re-plan them once they are back online.
          {!unblocks && " Other refusals above would still block the deploy."}
        </p>
      </div>
      <Button variant="secondary" disabled={busy} onClick={onSkip}>
        Skip offline switches…
      </Button>
    </div>
  );
}

/** A skip plan: the skipped switches, one acknowledgement each, then apply without them. */
function SkippedApply({
  plan,
  acked,
  onToggle,
  busy,
  pending,
  onCancel,
  onApply,
}: {
  plan: VlanRolloutPlan;
  acked: ReadonlySet<string>;
  onToggle: (serial: string, on: boolean) => void;
  busy: boolean;
  pending: boolean;
  onCancel: () => void;
  onApply: () => void;
}) {
  const skipped = plan.skipped_offline ?? [];
  const n = skipped.length;
  const complete = ackComplete(plan, acked);
  const deviceCount = plan.apply_order.length;
  return (
    <section aria-label="Skipped offline switches" className="flex flex-col gap-3 rounded-md border border-warn/40 bg-surface p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-ink">
          Skipped (offline) <StatusPill status="warn">{n}</StatusPill>
        </h3>
        <Button variant="ghost" onClick={onCancel}>
          Don't skip
        </Button>
      </div>
      {n === 0 && <p className="text-sm text-ink-muted">No switch is offline any more: nothing will be skipped.</p>}
      <ul className="flex flex-col gap-2">
        {skipped.map((item) => (
          <SkippedRow key={item.serial} item={item} checked={acked.has(item.serial)} onToggle={onToggle} />
        ))}
      </ul>
      <div className="flex flex-col gap-2 border-t border-line pt-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-ink-muted">
          {complete
            ? `Applies to ${deviceCount} device${deviceCount === 1 ? "" : "s"}; the skip is recorded in the audit log.`
            : "Tick every skipped switch to apply without it."}
        </p>
        <RoleGate min={WRITE_ROLE} mode="disable" reason={WRITE_REASON}>
          <Button variant="primary" disabled={!plan.can_apply || !complete || busy} onClick={onApply}>
            {pending ? "Starting…" : `Apply without ${n} offline switch${n === 1 ? "" : "es"}`}
          </Button>
        </RoleGate>
      </div>
      {!plan.can_apply && (
        <p className="text-xs text-critical">Apply is blocked until every refusal above is resolved.</p>
      )}
    </section>
  );
}

function SkippedRow({
  item,
  checked,
  onToggle,
}: {
  item: SkippedOfflineSwitch;
  checked: boolean;
  onToggle: (serial: string, on: boolean) => void;
}) {
  const w = item.would_receive;
  return (
    <li className="rounded-md border border-line bg-surface-2 p-3 text-sm">
      <p className="text-ink">{formatPortRefs(skippedLine(item))}</p>
      <label className="mt-2 flex min-h-touch items-start gap-2 text-sm text-ink">
        <input
          type="checkbox"
          className="mt-1"
          checked={checked}
          onChange={(e) => onToggle(item.serial, e.target.checked)}
          aria-label={`Acknowledge skipping ${item.name}`}
        />
        <span>{ackLabel(item)}</span>
      </label>
      {(w.commands.length > 0 || w.other_refusals.length > 0) && (
        <details className="mt-2 text-xs text-ink-muted">
          <summary className="cursor-pointer">Technical details</summary>
          {w.other_refusals.length > 0 && (
            <ul className="mt-1 list-disc pl-5">
              {w.other_refusals.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          )}
          {w.commands.length > 0 && (
            <pre className="mt-1 max-h-48 overflow-auto rounded bg-surface p-2 font-mono text-[0.6875rem]">
              {w.commands.join("\n")}
            </pre>
          )}
        </details>
      )}
    </li>
  );
}

/** The one-switch re-plan's own confirm + apply. */
function ScopedApply({
  plan,
  confirmed,
  onConfirm,
  busy,
  pending,
  onApply,
}: {
  plan: VlanRolloutPlan;
  confirmed: boolean;
  onConfirm: (on: boolean) => void;
  busy: boolean;
  pending: boolean;
  onApply: () => void;
}) {
  const name = plan.switches[0]?.name ?? plan.scope?.switch ?? "this switch";
  return (
    <div className="flex flex-col gap-3 rounded-md border border-line bg-surface-2 p-3 sm:flex-row sm:items-center sm:justify-between">
      <label className="flex min-h-touch items-center gap-2 text-sm text-ink">
        <input
          type="checkbox"
          checked={confirmed}
          disabled={!plan.can_apply}
          onChange={(e) => onConfirm(e.target.checked)}
        />
        I reviewed the changes on {name} (this switch only)
      </label>
      <RoleGate min={WRITE_ROLE} mode="disable" reason={WRITE_REASON}>
        <Button variant="primary" disabled={!plan.can_apply || !confirmed || busy} onClick={onApply}>
          {pending ? "Starting…" : `Apply to ${name}`}
        </Button>
      </RoleGate>
    </div>
  );
}

/**
 * Switches skipped while offline whose VLAN changes are still pending. Renders nothing when
 * none are. "Re-plan this switch" appears once the switch is back online; it opens the
 * one-switch review (`onReplan`) -- nothing is applied without the operator's confirm.
 */
export function PendingSwitchesCard({ siteId, onReplan }: { siteId: string; onReplan: (serial: string) => void }) {
  const pending = usePendingSwitches(siteId);
  const items: PendingSwitch[] = pending.data?.items ?? [];
  if (items.length === 0) return null;
  return (
    <section aria-label="Pending VLAN changes" className="md-card overflow-hidden">
      <CardHeader
        title="Pending VLAN changes"
        hint="Switches that were skipped while offline. Meridian never applies these automatically: re-plan each switch once it is back online."
      />
      <ul className="divide-y divide-line">
        {items.map((p) => (
          <li key={p.serial} className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-2 text-sm text-ink">
              <StatusPill status={p.online ? "warn" : "unknown"}>{p.online ? "Online" : "Offline"}</StatusPill>
              <span className="min-w-0">{pendingLine(p)}</span>
            </div>
            {p.can_replan && (
              <RoleGate min={WRITE_ROLE} mode="disable" reason={WRITE_REASON}>
                <Button variant="primary" onClick={() => onReplan(p.serial)}>
                  Re-plan this switch
                </Button>
              </RoleGate>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The "pending VLAN changes" flag on a SWITCH's device sheet. Renders nothing unless the switch
 * was skipped while offline and its VLANs are still pending. The re-plan opens on the site VLAN
 * page (?replan=<serial>), where the operator reviews and applies it -- never automatic.
 */
export function SwitchPendingVlansFlag({ serial }: { serial: string }) {
  const orgId = useSession((s) => s.currentOrgId);
  const q = useDevicePendingVlans(serial);
  const p = q.data?.pending;
  if (!p) return null;
  const siteId = q.data?.site_id;
  return (
    <div role="status" className="mb-3 flex flex-col gap-2 rounded-md border border-warn/40 bg-warn-soft p-3 text-sm text-ink sm:flex-row sm:items-center sm:justify-between">
      <span className="min-w-0">{pendingLine(p)}</span>
      {p.can_replan && orgId && siteId != null && (
        <RoleGate min={WRITE_ROLE} mode="hide">
          <Link
            className="inline-flex min-h-touch items-center rounded bg-brand px-3 text-sm font-medium text-ink-invert hover:bg-brand-strong"
            to={`/o/${orgId}/s/${siteId}/gateway-vlans?replan=${encodeURIComponent(p.serial)}`}
          >
            Re-plan this switch
          </Link>
        </RoleGate>
      )}
    </div>
  );
}

function refusalsOf(error: unknown): RolloutItem[] {
  if (!(error instanceof ApiError)) return [];
  const body = error.body as { refusals?: RolloutItem[] } | null;
  return Array.isArray(body?.refusals) ? body!.refusals! : [];
}

function PlanView({ plan, siteId, busy }: { plan: VlanRolloutPlan; siteId: string; busy: boolean }) {
  const names = useMemo(() => {
    const out: Record<string, string> = {};
    for (const d of [...plan.switches, ...plan.gateways]) out[d.serial] = d.name;
    return out;
  }, [plan]);
  const bySerial = useMemo(() => {
    const out: Record<string, RolloutSwitchPlan | RolloutGatewayPlan> = {};
    for (const d of [...plan.switches, ...plan.gateways]) out[d.serial] = d;
    return out;
  }, [plan]);

  return (
    <div className={cn("flex flex-col gap-4 transition-opacity", busy && "opacity-60")}>
      <div className="flex flex-wrap gap-2 text-xs">
        <Chip>{plan.vlans.length} VLAN{plan.vlans.length === 1 ? "" : "s"}</Chip>
        <Chip>{plan.gateways.length} gateway{plan.gateways.length === 1 ? "" : "s"}</Chip>
        <Chip>{plan.switches.length} switch{plan.switches.length === 1 ? "" : "es"}</Chip>
        {plan.ha_pair && <Chip>HA pair</Chip>}
        <StatusPill status={plan.refusals.length ? "critical" : "healthy"}>
          {plan.refusals.length} refusal{plan.refusals.length === 1 ? "" : "s"}
        </StatusPill>
        <StatusPill status={plan.warnings.length ? "warn" : "healthy"}>
          {plan.warnings.length} warning{plan.warnings.length === 1 ? "" : "s"}
        </StatusPill>
      </div>

      {plan.refusals.length > 0 && <ItemList items={plan.refusals} tone="critical" names={names} />}
      {plan.warnings.length > 0 && <ItemList items={plan.warnings} tone="warn" names={names} />}
      {/* INFO only (owner decision 2026-10-14): e.g. a VLAN path hop that cannot be checked. */}
      {(plan.notes?.length ?? 0) > 0 && <ItemList items={plan.notes!} tone="info" names={names} />}

      <ol className="flex flex-col gap-3">
        {plan.apply_order.map((serial, index) => {
          const entry = bySerial[serial];
          if (!entry) return null;
          return (
            <li key={serial} className="rounded-md border border-line bg-surface">
              <div className="flex flex-wrap items-center gap-2 border-b border-line px-3 py-2">
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-surface-2 text-xs font-semibold tabular-nums text-ink-muted">
                  {index + 1}
                </span>
                <span className="text-sm font-medium text-ink">{entry.name}</span>
                <span className="text-xs text-ink-subtle">
                  {entry.kind === "gateway" ? `gateway · ${entry.role}` : `switch · ${entry.model}`}
                </span>
                {!entry.online && <StatusPill status="critical">Offline</StatusPill>}
                {entry.refusals.length > 0 && (
                  <StatusPill status="critical">
                    {entry.refusals.length} refusal{entry.refusals.length === 1 ? "" : "s"}
                  </StatusPill>
                )}
              </div>
              {entry.kind === "gateway" ? (
                <GatewayPlanBody entry={entry} />
              ) : (
                <SwitchPlanBody entry={entry} siteId={siteId} />
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function GatewayPlanBody({ entry }: { entry: RolloutGatewayPlan }) {
  const untagged = (entry.networks_before?.networks ?? []).filter((n) => n.vlan == null);
  return (
    <div className="flex flex-col gap-2 p-3 text-sm">
      {untagged.map((n) => (
        <p key={n.name ?? "lan"} className="text-xs text-ink-muted">
          Untagged LAN <span className="font-mono text-ink">{n.name}</span>{" "}
          <span className="font-mono">{n.subnet}</span> stays untagged and unchanged.
        </p>
      ))}
      {/* Adoptions first: an existing unaddressed network on the VLAN is taken over, not
          duplicated (gateway_config.site_vlan_adoptions). */}
      {(entry.adopted?.length ?? 0) > 0 && (
        <ul className="flex flex-col gap-1 text-xs text-ink-muted">
          {entry.adopted!.map((a) => (
            <li key={a.vlan}>
              Adopts existing unaddressed network <span className="font-mono text-ink">{a.from_name}</span>{" "}
              (VLAN <span className="tabular-nums">{a.vlan}</span>)
              {a.renamed ? (
                <>
                  {" "}as <span className="text-ink">{a.name}</span>
                </>
              ) : null}
              {a.network?.interface_ip ? (
                <>
                  {" "}· <span className="font-mono">{a.network.interface_ip}</span>{" "}
                  <span className="font-mono">{a.network.subnet}</span> · {dhcpScope(a.network.subnet, a.network.dhcp)}
                </>
              ) : null}
              {a.ssids.length > 0
                ? ` · keeps its SSID block${a.ssids.length === 1 ? "" : "s"} ${a.ssids.join(", ")}`
                : null}
            </li>
          ))}
        </ul>
      )}
      {/* A tagging repair is neither an add nor a removal, so it gets its own line -- otherwise
          a push that ONLY fixes tagging reviews as a bare "Changes pending." */}
      {(entry.retagged?.length ?? 0) > 0 && (
        <ul className="flex flex-col gap-1 text-xs text-critical">
          {entry.retagged!.map((r) => (
            <li key={r.vlan}>
              Fixes tagging on <span className="font-mono text-ink">{r.name ?? `VLAN ${r.vlan}`}</span>{" "}
              (VLAN <span className="tabular-nums">{r.vlan}</span>): it is configured but leaves the
              gateway UNTAGGED today, so it carries no traffic. Address and DHCP are unchanged.
            </li>
          ))}
        </ul>
      )}
      {entry.added.length === 0 ? (
        (entry.adopted?.length ?? 0) > 0 || (entry.retagged?.length ?? 0) > 0 ? null : (
          <p className="text-xs text-ink-muted">
            {entry.changed ? "Changes pending." : "Nothing to add — this gateway already carries these VLANs."}
          </p>
        )
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] text-left text-xs">
            <thead className="text-ink-subtle">
              <tr className="[&>th]:py-1 [&>th]:pr-3 [&>th]:font-medium">
                <th>VLAN</th>
                <th>Name</th>
                <th>Interface IP</th>
                <th>Subnet</th>
                <th>DHCP scope</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {entry.added.map((n) => (
                <tr key={n.name ?? String(n.vlan)} className="border-t border-line/60 [&>td]:py-1 [&>td]:pr-3">
                  <td className="tabular-nums">{n.vlan}</td>
                  <td className="font-sans">{n.name}</td>
                  <td>{n.interface_ip}</td>
                  <td>{n.subnet}</td>
                  <td>{dhcpScope(n.subnet, n.dhcp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {(entry.removed?.length ?? 0) > 0 && (
        <p className="text-xs text-ink-muted">
          Removes (deleted from the site):{" "}
          {entry.removed!.map((n) => (
            <span key={n.name ?? String(n.vlan)} className="mr-1 inline-block rounded bg-surface-2 px-1.5 py-0.5 font-mono text-ink">
              {n.vlan} {n.name} {n.subnet}
            </span>
          ))}
        </p>
      )}
      {entry.skipped.length > 0 && (
        <p className="text-xs text-ink-subtle">
          Layer 2 only (no gateway interface): {entry.skipped.map((s) => s.vlan).join(", ")}
        </p>
      )}
      {entry.diff.length > 0 && (
        <details className="rounded border border-line">
          <summary className="cursor-pointer px-3 py-1.5 text-xs text-ink-muted">
            Configuration diff ({entry.diff.length})
          </summary>
          <pre className="max-h-64 overflow-auto px-3 py-2 font-mono text-[0.6875rem] text-ink-subtle">
            {JSON.stringify(entry.diff, null, 1)}
          </pre>
        </details>
      )}
    </div>
  );
}

/** "10.150.150.10 – .129" from the uCentral offset form (first + count). */
function dhcpScope(
  subnet: string | null,
  dhcp: { lease_first: number | null; lease_count: number | null; lease_time: string | null } | null,
): string {
  if (!dhcp || dhcp.lease_first == null || dhcp.lease_count == null) return "none";
  const base = subnet?.split("/")[0]?.split(".");
  if (!base || base.length !== 4) return `+${dhcp.lease_first} ×${dhcp.lease_count}`;
  const last = dhcp.lease_first + dhcp.lease_count - 1;
  // Offsets past the last octet only happen on /23 and wider; show offsets there instead.
  if (last > 254) return `offsets ${dhcp.lease_first}–${last}`;
  return `.${dhcp.lease_first}–.${last} · ${dhcp.lease_time ?? ""}`;
}

const REASON_TEXT: Record<string, string> = {
  lldp_meridian_neighbor: "LLDP neighbour is a Meridian device",
  override_trunk: "Marked trunk by an operator",
  override_ap: "Marked AP port by an operator",
  override_exclude: "Excluded by an operator",
  no_lldp: "No LLDP neighbour",
  neighbor_foreign_switch: "Foreign switch — never touched",
  neighbor_phone: "Phone",
  neighbor_unknown: "Unidentified neighbour",
};

function SwitchPlanBody({ entry, siteId }: { entry: RolloutSwitchPlan; siteId: string }) {
  const [showAll, setShowAll] = useState(false);
  // The ports that matter: changed ones, ones with a neighbour or override, and live links.
  const relevant = entry.ports.filter(
    (p) => p.action === "trunk" || p.neighbors.length > 0 || p.override || p.carrier,
  );
  const ports = showAll ? entry.ports : relevant;

  return (
    <div className="flex flex-col gap-3 p-3 text-sm">
      {entry.create_vlans.length > 0 && (
        <p className="text-xs text-ink-muted">
          Creates in the VLAN database:{" "}
          {entry.create_vlans.map((v) => (
            <span key={v.vlan} className="mr-1 inline-block rounded bg-surface-2 px-1.5 py-0.5 font-mono text-ink">
              {v.vlan}
              {v.name ? ` ${v.name}` : ""}
            </span>
          ))}
        </p>
      )}
      {(entry.remove_vlans?.length ?? 0) > 0 && (
        <p className="text-xs text-ink-muted">
          Removes from its trunk ports and VLAN database (deleted from the site):{" "}
          {entry.remove_vlans!.map((v) => (
            <span key={v} className="mr-1 inline-block rounded bg-surface-2 px-1.5 py-0.5 font-mono text-ink">
              {v}
            </span>
          ))}
          <span className="block text-ink-subtle">
            The VLAN database removal has not been verified on this switch model yet; a switch that refuses it
            stops the deploy before saving.
          </span>
        </p>
      )}
      {entry.ports.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem] text-left text-xs">
            <thead className="text-ink-subtle">
              <tr className="[&>th]:py-1 [&>th]:pr-3 [&>th]:font-medium">
                <th>Port</th>
                <th>Change</th>
                <th>Why</th>
                <th>Neighbour</th>
                <th>Override</th>
              </tr>
            </thead>
            <tbody>
              {ports.map((p) => (
                <PortRow key={p.port} port={p} serial={entry.serial} siteId={siteId} />
              ))}
            </tbody>
          </table>
          {relevant.length < entry.ports.length && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-1 min-h-touch text-xs font-medium text-brand hover:underline"
            >
              {showAll ? "Show only relevant ports" : `Show all ${entry.ports.length} ports`}
            </button>
          )}
        </div>
      )}
      {entry.commands.length > 0 && (
        <details className="rounded border border-line" open>
          <summary className="cursor-pointer px-3 py-1.5 text-xs text-ink-muted">
            Switch commands ({entry.commands.length} lines, one session, saved at the end)
          </summary>
          <pre className="max-h-72 overflow-auto px-3 py-2 font-mono text-[0.6875rem] leading-relaxed text-ink">
            {entry.commands.join("\n")}
          </pre>
        </details>
      )}
    </div>
  );
}

function PortRow({ port, serial, siteId }: { port: RolloutPortDecision; serial: string; siteId: string }) {
  const setOverride = useSetTrunkOverride(siteId);
  const neighbour = port.neighbors[0];
  const change =
    port.action === "untouched"
      ? "—"
      : port.role === "ap" && port.native_target != null
        ? `AP port · native ${port.native_target}${port.native_current != null && port.native_current !== port.native_target ? ` (was ${port.native_current})` : ""} · VLANs tagged`
        : port.role === "ap"
          ? "AP port · VLANs tagged"
          : "Trunk · VLANs tagged";
  return (
    <tr className="border-t border-line/60 align-top [&>td]:py-1.5 [&>td]:pr-3">
      <td className="whitespace-nowrap font-mono text-ink" title={`${port.port} · interface ethernet ${port.eccli ?? "?"}`}>
        {formatSwitchPort(port.port)}
        {port.carrier && <span className="ml-1 text-healthy" aria-label="link up">●</span>}
      </td>
      <td className={cn(port.action === "trunk" ? "text-ink" : "text-ink-subtle")}>{change}</td>
      <td className="text-ink-muted">{REASON_TEXT[port.reason] ?? port.reason}</td>
      <td className="min-w-0 break-words font-mono text-ink-muted">
        {neighbour ? formatPortRefs(neighbour.label ?? neighbour.name ?? "") : "—"}
        {neighbour?.kind && <span className="ml-1 font-sans text-ink-subtle">({neighbour.kind})</span>}
      </td>
      <td>
        <RoleGate min={WRITE_ROLE} mode="hide" fallback={<span className="text-ink-subtle">{port.override ?? "auto"}</span>}>
          <select
            aria-label={`Override for port ${formatSwitchPort(port.port)}`}
            className="min-h-touch rounded border border-line bg-surface px-2 text-xs text-ink focus:border-brand"
            value={port.override ?? ""}
            disabled={setOverride.isPending}
            onChange={(e) =>
              setOverride.mutate({
                serial,
                port: port.port,
                mode: (e.target.value || null) as TrunkOverrideMode | null,
              })
            }
          >
            <option value="">Auto (LLDP)</option>
            <option value="trunk">Trunk</option>
            <option value="ap">AP port</option>
            <option value="exclude">Exclude</option>
          </select>
        </RoleGate>
      </td>
    </tr>
  );
}

const DEVICE_STATUS: Record<RolloutDeviceResult["status"], { tone: HealthState | "info"; label: string }> = {
  pending: { tone: "unknown", label: "Waiting" },
  running: { tone: "info", label: "Applying" },
  applied: { tone: "healthy", label: "Applied" },
  failed: { tone: "critical", label: "Failed" },
  skipped: { tone: "unknown", label: "Not attempted" },
  skipped_offline: { tone: "warn", label: "Skipped (offline)" },
  held_pending_replan: { tone: "warn", label: "Held for re-plan" },
};

function RolloutProgress({
  rollout,
  verifying,
  verifyError,
  onVerify,
}: {
  rollout: VlanRollout;
  verifying: boolean;
  verifyError: unknown;
  onVerify: () => void;
}) {
  const overall: HealthState | "info" =
    rollout.status === "running" ? "info" : rollout.status === "succeeded" ? "healthy" : "critical";
  const statusWord: Record<VlanRollout["status"], string> = {
    running: "Deploying",
    succeeded: "Succeeded",
    failed: "Failed",
    interrupted: "Interrupted",
    refused: "Refused — nothing applied",
  };
  const verification = new Map((rollout.verification?.devices ?? []).map((d) => [d.serial, d]));
  const verified = verificationWord(rollout.verification);
  const missingLines = verificationMissingLines(rollout.verification);

  return (
    <div className="rounded-md border border-line">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-ink">Last deploy</span>
          <StatusPill status={overall}>{statusWord[rollout.status] ?? rollout.status}</StatusPill>
          <span className="text-xs text-ink-subtle">
            {rollout.trigger === "auto" ? "automatic" : "manual"}
            {rollout.started_by ? ` · ${rollout.started_by}` : ""}
          </span>
          {verified && <StatusPill status={verified.tone}>{verified.label}</StatusPill>}
          {rollout.verification && (
            <span className="text-xs text-ink-subtle">
              {rollout.verification.trigger === "auto" ? "read back automatically" : "read back on request"}
            </span>
          )}
        </div>
        <RoleGate min={WRITE_ROLE} mode="hide">
          <Button variant="secondary" disabled={rollout.status === "running" || verifying} onClick={onVerify}>
            {verifying ? "Reading devices…" : rollout.verification ? "Verify again" : "Verify on devices"}
          </Button>
        </RoleGate>
      </div>
      {rollout.error && <p className="border-b border-line px-3 py-2 text-sm text-critical">{rollout.error}</p>}
      {/* OP#1822: the automatic read-back names every switch missing a site VLAN. */}
      {missingLines.length > 0 && (
        <ul className="border-b border-line px-3 py-2 text-sm text-critical">
          {missingLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
      {/* OP#1818: the end-to-end VLAN path after the run (stored reads), broken/unknown only. */}
      <RolloutPathLines pc={rollout.verification?.path_check} />
      {rollout.verification?.status === "error" && rollout.verification.error && (
        <p className="border-b border-line px-3 py-2 text-xs text-critical">
          The automatic read-back failed: {rollout.verification.error}
        </p>
      )}
      {(rollout.refusals?.length ?? 0) > 0 && (
        <div className="border-b border-line p-3">
          <ItemList
            items={rollout.refusals!}
            tone="critical"
            names={Object.fromEntries(rollout.devices.map((d) => [d.serial, d.name]))}
          />
        </div>
      )}
      {verifyError != null && (
        <div className="p-3">
          <ErrorBanner error={verifyError} />
        </div>
      )}
      <ol className="divide-y divide-line">
        {rollout.devices.map((d, i) => {
          const s = DEVICE_STATUS[d.status] ?? { tone: "unknown", label: d.status };
          const v = verification.get(d.serial);
          const vLabel = v ? verificationDeviceLabel(v.status) : null;
          return (
            <li key={d.serial} className="flex flex-col gap-1 px-3 py-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="w-5 text-xs tabular-nums text-ink-subtle">{i + 1}</span>
                <span className="font-medium text-ink">{d.name}</span>
                <span className="text-xs text-ink-subtle">{d.kind}{d.role ? ` · ${d.role}` : ""}</span>
                <StatusPill status={s.tone}>{d.no_op && d.status === "applied" ? "No change needed" : s.label}</StatusPill>
                {v && vLabel && <StatusPill status={vLabel.tone}>{vLabel.label}</StatusPill>}
              </div>
              {d.error && <p className="pl-7 text-xs text-critical">{d.error}</p>}
              {d.status === "skipped_offline" && (
                <p className="pl-7 text-xs text-ink-muted">
                  Skipped while offline
                  {d.detail?.acknowledged_by ? `, acknowledged by ${d.detail.acknowledged_by}` : ""}. Pending:{" "}
                  {(d.detail?.would_receive?.vlans ?? []).join(", ") || "—"}. Re-plan it once it is back online.
                </p>
              )}
              {d.status === "held_pending_replan" && (
                <p className="pl-7 text-xs text-ink-muted">
                  Not configured by this run: it has pending VLAN changes (
                  {(d.detail?.pending_vlans ?? []).join(", ") || "—"}) that only "Re-plan this switch" applies.
                </p>
              )}
              {d.detail?.partial && (
                <p className="pl-7 text-xs text-warn">
                  Partially applied and NOT saved — a reboot reverts it: {(d.detail.applied ?? []).join(" · ")}
                </p>
              )}
              {v && v.status !== "verified" && <VerificationDetail device={v} />}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function VerificationDetail({ device }: { device: NonNullable<VlanRollout["verification"]>["devices"][number] }) {
  const missingGateway = (device.vlans ?? []).filter((v) => !v.observed).map((v) => `${v.vlan} (${v.expected})`);
  const portIssues = (device.ports ?? []).filter((p) => p.missing_tagged.length > 0 || !p.native_ok);
  return (
    <div className="pl-7 text-xs text-ink-muted">
      {device.error && <p className="text-critical">{device.error}</p>}
      {device.status === "no_state" && <p>The gateway has not reported a state document since the push.</p>}
      {device.status === "awaiting_state" && (
        <p>The gateway has not reported since the push yet; Verify again in a minute.</p>
      )}
      {device.status === "not_attempted" && <p>Not read back: the deploy did not apply this device.</p>}
      {device.status === "skipped_offline" && (
        <p>
          Not read back: skipped while offline (not missing). VLAN {(device.pending_vlans ?? []).join(", ") || "—"} pending
          until it is re-planned.
        </p>
      )}
      {device.status === "held_pending_replan" && (
        <p>
          Not read back: held for its re-plan (not missing). VLAN {(device.pending_vlans ?? []).join(", ") || "—"}{" "}
          pending until "Re-plan this switch" is applied.
        </p>
      )}
      {missingGateway.length > 0 && <p>Not yet observed on the gateway: {missingGateway.join(", ")}</p>}
      {(device.missing_vlans?.length ?? 0) > 0 && <p>Missing from the VLAN database: {device.missing_vlans!.join(", ")}</p>}
      {portIssues.map((p) => (
        <p key={p.port}>
          Port {formatSwitchPort(p.port)}:{" "}
          {p.missing_tagged.length > 0 && `not tagged ${p.missing_tagged.join(", ")}`}
          {!p.native_ok && ` native ${p.native_expected} not untagged`}
        </p>
      ))}
    </div>
  );
}

/* =========================================================================================
 * small pieces
 * ========================================================================================= */

function CardHeader({ title, hint, action }: { title: string; hint: string; action?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-line px-4 py-3">
      <div className="min-w-0 max-w-2xl">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        <p className="mt-0.5 text-xs text-ink-muted">{hint}</p>
      </div>
      {action}
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span className="text-xs font-medium text-ink-muted">{label}</span>
      {children}
      {hint && <span className="text-[0.6875rem] text-ink-subtle">{hint}</span>}
    </label>
  );
}

function Chip({ children }: { children: ReactNode }) {
  return <span className="rounded-full border border-line bg-surface-2 px-2 py-0.5 text-ink-muted">{children}</span>;
}

function ItemList({
  items,
  tone,
  names,
}: {
  items: RolloutItem[];
  tone: "critical" | "warn" | "info";
  names: Record<string, string>;
}) {
  return (
    <ul
      aria-label={tone === "info" ? "Notes" : undefined}
      className={cn(
        "md-card p-3 text-sm text-ink",
        tone === "critical"
          ? "border-critical/40 bg-critical-soft"
          : tone === "info"
            ? "border-info/40 bg-info-soft"
            : "border-warn/40 bg-warn-soft",
      )}
    >
      {items.map((item, i) => (
        <li key={`${item.code}-${item.serial ?? "site"}-${i}`} className="flex gap-2 py-0.5">
          <span
            className={tone === "critical" ? "text-critical" : tone === "info" ? "text-info" : "text-warn"}
            aria-hidden
          >
            {tone === "critical" ? "✕" : tone === "info" ? "i" : "!"}
          </span>
          <span className="min-w-0">
            {item.serial && <span className="font-medium">{names[item.serial] ?? item.serial}: </span>}
            {formatPortRefs(item.message)}
            <span className="ml-1 font-mono text-[0.6875rem] text-ink-subtle">{item.code}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}
